import { forwardRef, useImperativeHandle, useRef } from 'react';
import {
  Button,
  Toolbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { isMobile } from '../../../../utils/DeviceUtils';
import './LessonActivityBottomBar.scss';
import { useScreenReader } from '../../../screen-reader/ScreenReaderHooks';
import { useRegisterSwitchAccessScanEvent } from '../../../switch-access/SwitchAccessHooks';
import { TOOLBAR_ACTIONS_ID, TOOLBAR_NEXT_ACTION_ID } from '../../../../components/navigator/LessonNavigator';
import { filterUndefined } from '../../../../utils/FilterUndefinedObject';

/**
 * Play Lesson Action Container
 */
const defaultProps = {
  onNext: () => { },
  onPrevious: () => { },
  hideNext: true,
  hidePrevious: true,
  children: null,
  showCorrectSubmissionMessage: false,
};
const LessonActivityBottomBar = forwardRef((props, ref) => {
  const mergedProps = { ...defaultProps, ...filterUndefined(props) };
  const {
    onNext,
    onPrevious,
    hideNext,
    hidePrevious,
    children,
    showCorrectSubmissionMessage,
  } = mergedProps;
  const previousButtonRef = useRef(null);
  const nextButtonRef = useRef(null);

  const {
    screenReaderOn,
    readWhenScreenReaderIsOn,
  } = useScreenReader();

  useImperativeHandle(ref, () => ({
    getEnableElements: () => {
      const result = [];
      if (previousButtonRef.current) {
        result.push(previousButtonRef.current);
      }
      if (nextButtonRef.current) {
        result.push(nextButtonRef.current);
      }
      return result;
    },
  }));

  useRegisterSwitchAccessScanEvent(previousButtonRef, () => {
    readWhenScreenReaderIsOn(previousButtonRef.current);
  });

  useRegisterSwitchAccessScanEvent(nextButtonRef, () => {
    readWhenScreenReaderIsOn(nextButtonRef.current);
  });

  return (
    <div className='lesson-activity-bottom-bar'>
      <Toolbar className={`lesson-activity-toolbar ${showCorrectSubmissionMessage ? 'success-message-toolbar' : ''}`} tabIndex='-1'>
        {hidePrevious ? (<div className='navigation-button-empty' />) : (
          <Button
            className={`navigation-button ${isMobile() ? 'navigation-button-mobile' : ''}`}
            disableRipple
            disableElevation
            disableFocusRipple
            onClick={onPrevious}
            ref={previousButtonRef}
            alt='Go Backward'
            aria-label='Go Backward'
            data-switch-access-scan={screenReaderOn ? 'true' : 'false'}
            data-test='lesson-play-previous-action'
            tabIndex={-1}
          >
            <ArrowBackIcon className='icon' />
          </Button>
        )}
        {children || <div />}
        <div className='next-toolbar-container'>
          <div className='hide' id={TOOLBAR_ACTIONS_ID} />
          {hideNext ? (<div className='navigation-button-empty' id={TOOLBAR_NEXT_ACTION_ID} />) : (
            <Button
              id={TOOLBAR_NEXT_ACTION_ID}
              className={`navigation-button right ${isMobile() ? 'navigation-button-mobile' : ''}`}
              disableRipple
              disableElevation
              disableFocusRipple
              onClick={onNext}
              ref={nextButtonRef}
              alt='Go Forward'
              aria-label='Go Forward'
              data-switch-access-scan={screenReaderOn ? 'true' : 'false'}
              data-test='lesson-play-next-action'
              tabIndex='-1'
            >
              <ArrowForwardIcon className='icon' />
            </Button>
          )}
        </div>
      </Toolbar>
    </div>
  );
});

export default LessonActivityBottomBar;
